import React, { useEffect, useState } from "react";
import axiosInstance from "../../../axiosInstance";
import { toast } from "react-toastify";
import { Formik, Form, Field, ErrorMessage } from "formik";
import logo from "../../../Images/Hrlabs_Logo.jpeg";
import * as Yup from "yup";
import Heading from "../../../components/HeadingCom/Heading";
import "./CompanyDetail.scss";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import FileInput from "../../../components/InputComponent/FileInput";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import WarningPopup from "../../../components/WarningPopup/WarningPopup"; // Adjust path as needed

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

// Helper function to calculate luminance
const getLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// Helper function to check if color is too light/white-like
const isColorTooLight = (hexColor) => {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return false;

  const { r, g, b } = rgb;

  // Check for pure white and near-white colors
  const whiteThreshold = 240; // RGB values above this are considered too light
  const isNearWhite =
    r > whiteThreshold && g > whiteThreshold && b > whiteThreshold;

  // Check luminance (0 = black, 1 = white)
  const luminance = getLuminance(r, g, b);
  const isHighLuminance = luminance > 0.85; // Luminance above 85% is too bright

  // Check for specific problematic colors
  const problematicColors = [
    // Pure white and near-whites
    "#FFFFFF",
    "#ffffff",
    "#FEFEFE",
    "#fefefe",
    "#FDFDFD",
    "#fdfdfd",
    "#FCFCFC",
    "#fcfcfc",
    "#FBFBFB",
    "#fbfbfb",
    "#FAFAFA",
    "#fafafa",
    "#F9F9F9",
    "#f9f9f9",
    "#F8F8F8",
    "#f8f8f8",
    "#F7F7F7",
    "#f7f7f7",
    "#F6F6F6",
    "#f6f6f6",
    "#F5F5F5",
    "#f5f5f5",

    // Very light warm/pink tones
    "#FFF5F5",
    "#fff5f5",
    "#FFEFEF",
    "#ffefef",
    "#FFECEC",
    "#ffecec",
    "#FFEAEA",
    "#ffeaea",
    "#FFE8E8",
    "#ffe8e8",
    "#FFE6E6",
    "#ffe6e6",
    "#FFE5E5",
    "#ffe5e5",
    "#FFEEEE",
    "#ffeeee",

    // Very light yellow tones
    "#FFFFF0",
    "#fffff0",
    "#FFFFE0",
    "#ffffe0",
    "#FFFACD",
    "#fffacd",
    "#FFF8DC",
    "#fff8dc",

    // Very light blue tones
    "#F0FFFF",
    "#f0ffff",
    "#E6F7FF",
    "#e6f7ff",
    "#E0FFFF",
    "#e0ffff",
    "#F0F8FF",
    "#f0f8ff",

    // Very light green tones
    "#F5FFFA",
    "#f5fffa",
    "#F0FFF0",
    "#f0fff0",
    "#F5FBEF",
    "#f5fbef",

    // Custom near-white shades (RGB > ~230)
    "#F4F4F4",
    "#f4f4f4",
    "#F3F3F3",
    "#f3f3f3",
    "#F2F2F2",
    "#f2f2f2",
    "#F1F1F1",
    "#f1f1f1",
    "#F0F0F0",
    "#f0f0f0",
    "#EFEFEF",
    "#efefef",
    "#EEEEEE",
    "#eeeeee",
    "#EDEDED",
    "#ededed",
    "#ECECEC",
    "#ececec",

    // Pure black and near-blacks
    "#000000",
    "#000001",
    "#010101",
    "#0A0A0A",
    "#0B0B0B",
    "#0C0C0C",
    "#111111",
    "#121212",
    "#141414",
    "#1A1A1A",
    "#1C1C1C",
    "#1E1E1E",
    "#202020",
    "#222222",
    "#242424",
    "#262626",
    "#282828",
    "#2A2A2A",
  ];

  return (
    isNearWhite ||
    isHighLuminance ||
    problematicColors.includes(hexColor.toUpperCase())
  );
};

// Validation schema for form inputs
const companyDetailValidationSchema = Yup.object().shape({
  companyName: Yup.string()
    .min(3, "Company Name must be at least 3 characters")
    .max(100, "Company Name can't be longer than 100 characters")
    .required("Company Name is required"),
  ceo: Yup.string()
    .min(2, "CEO name must be at least 2 characters")
    .max(50, "CEO name can't be longer than 50 characters")
    .required("CEO is required"),
  empIDPrefix: Yup.string()
    .matches(
      /^[A-Za-z0-9@#_-]{2,8}$/,
      "2-8 characters: letters, numbers, @, #, _, - allowed",
    )
    .required("Employee ID Prefix is required"),
  logo: Yup.mixed()
    .nullable()
    .test(
      "fileSize",
      "File is too big. Maximum size is 500KB",
      (file) => !file || file.size <= 500000, // max 500 KB
    )
    .test(
      "fileFormat",
      "Logo must be a JPEG or PNG",
      (file) => !file || ["image/jpeg", "image/png"].includes(file.type),
    ),

  addressLine1: Yup.string()
    .max(200, "Address Line 1 can't be longer than 200 characters")
    .required("Address Line 1 is required"),
  addressLine2: Yup.string().max(
    200,
    "Address Line 2 can't be longer than 200 characters",
  ),
  city: Yup.string()
    .min(2, "City must be at least 2 characters")
    .max(100, "City can't be longer than 100 characters")
    .required("City is required"),
  state: Yup.string()
    .min(2, "State must be at least 2 characters")
    .max(100, "State can't be longer than 100 characters")
    .required("State is required"),
  country: Yup.string()
    .min(2, "Country must be at least 2 characters")
    .max(100, "Country can't be longer than 100 characters")
    .required("Country is required"),
  pincode: Yup.string()
    .matches(/^\d{5,6}$/, "Pin Code must be 5 or 6 digits")
    .required("Pin Code is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
});

// Enhanced validation schema for color theme with stricter white detection
const colorThemeValidationSchema = Yup.object().shape({
  colorTheme: Yup.string()
    .matches(/^#[0-9A-F]{6}$/i, "Please enter a valid hex color code")
    .test(
      "not-white-or-light",
      "White/Black, off-white/black, and very light/black colors are not allowed as they cause visibility issues. Please choose a darker color.",
      (value) => {
        if (!value) return false;
        return !isColorTooLight(value);
      },
    )
    .required("Color theme is required"),
});

const CompanyDetail = ({ isEditing, setIsEditing }) => {
  const [companyDetailData, setCompanyDetailData] = useState({
    companyName: "Dummy Company",
    ceo: "John Doe",
    empIDPrefix: "DC",
    addressLine1: "123 Dummy St",
    addressLine2: "Suite 100",
    city: "Dummytown",
    state: "DS",
    country: "Dummyland",
    pincode: "12345",
    email: "info@dummycompany.com",
    logo: null,
    logoType: logo, // Default logo type if you want to display a logo
    colorTheme: "#007bff", // Default color theme
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isFetched, setIsFetched] = useState(false);
  const [colorThemeLoading, setColorThemeLoading] = useState(false);
  const [isEditingColorTheme, setIsEditingColorTheme] = useState(false);
  const [colorWarning, setColorWarning] = useState("");
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      if (isFetched) return;

      setLoading(true);
      const id = process.env.REACT_APP_COMPANY_ID; // Use company ID from .env
      try {
        // Fetch company details
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/company/${id}`,
        );
        const data = response.data.data;

        // Fetch color theme
        const colorThemeResponse = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/company-settings/color-theme`,
        );

        const colorTheme = colorThemeResponse.data.data || "#007bff";

        setCompanyDetailData({
          ...data,
          colorTheme: colorTheme,
        });
        setIsFetched(true);
      } catch (error) {
        setError("Failed to load company details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyDetails();
  }, [isFetched]);

  // Function to handle color input changes and show warnings
  const handleColorChange = (colorValue, setFieldValue) => {
    setFieldValue("colorTheme", colorValue);

    if (isColorTooLight(colorValue)) {
      setColorWarning(
        "⚠️ This color is too light/dark and may cause visibility issues. Please choose a darker/lighter color.",
      );
    } else {
      setColorWarning("");
    }
  };

  const handleColorThemeUpdate = async (colorTheme) => {
    // Double-check color before submitting
    if (isColorTooLight(colorTheme)) {
      toast.error(
        "Cannot save: The selected color is too light/dark and will cause visibility issues. Please choose a darker color.",
      );
      return;
    }

    setColorThemeLoading(true);
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/company-settings/update-colorTheme`,
        {
          companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
          colorTheme: colorTheme,
        },
      );

      if (response.status === 200) {
        // Update sessionStorage FIRST
        sessionStorage.setItem("colorTheme", colorTheme);

        // Dispatch custom event to notify ThemeContext immediately
        window.dispatchEvent(
          new CustomEvent("themeColorUpdate", {
            detail: { colorTheme },
          }),
        );

        // Update local state
        setCompanyDetailData((prev) => ({
          ...prev,
          colorTheme: colorTheme,
        }));

        // Add a small delay to ensure the theme context has processed the change
        setTimeout(() => {
          setIsEditingColorTheme(false);
          setColorWarning("");
          toast.success("Color theme updated successfully!");
        }, 100);
      }
    } catch (error) {
      console.error("Color theme update error:", error);
    } finally {
      setColorThemeLoading(false);
    }
  };

  const handleDeleteSampleData = async () => {
    setIsDeleting(true);
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/cleanup/sample-data`,
      );

      if (response.status === 200) {
        toast.success("Sample data deleted successfully!");
        setShowDeleteWarning(false);
        // Optionally refresh the page or redirect
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error("Delete sample data error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const formData = new FormData();

    const companyDetails = {
      companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
      companyName: values.companyName,
      empIDPrefix: values.empIDPrefix,
      ceo: values.ceo,
      email: values.email,
      addressLine1: values.addressLine1,
      addressLine2: values.addressLine2,
      city: values.city,
      state: values.state,
      country: values.country,
      pincode: values.pincode,
    };

    formData.append(
      "companyDetails",
      new Blob([JSON.stringify(companyDetails)], { type: "application/json" }),
    );

    formData.append(
      "logo",
      values.logo || new Blob([""], { type: "application/octet-stream" }),
    );

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_BASE_URL}/company/save`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status === 200) {
        const data = response.data;

        if (values.companyName) {
          sessionStorage.setItem("companyName", values.companyName);
        }

        if (values.logo && data.data?.logo) {
          sessionStorage.setItem("logoImage", data.data.logo);
          sessionStorage.setItem(
            "logoImageType",
            data.data.logoType || "image/png",
          );
          window.dispatchEvent(
            new CustomEvent("sessionStorageChange", {
              detail: { key: "logoImage", value: data.data.logo },
            }),
          );
        }

        toast.success("Company details saved successfully!");

        // Preserve colorTheme and merge with saved values
        setCompanyDetailData((prev) => ({
          ...prev,
          ...companyDetails,
          logo: data.data?.logo || prev.logo,
          logoType: data.data?.logoType || prev.logoType,
        }));

        setIsEditing(false);
        // Removed setIsFetched(false) — no refetch needed
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to save company details. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <LoadingScreen />
      </div>
    ); // Show loading message
  }

  const formatCompanyData = (data) => {
    return [
      {
        field: "Company Name",
        value: data?.companyName || "N/A",
        className: "company-name",
      },
      { field: "CEO", value: data?.ceo || "N/A", className: "company-ceo" },
      {
        field: "Employee ID Prefix",
        value: data?.empIDPrefix || "N/A",
        className: "company-id-prefix",
      },
      {
        field: "Address Line 1",
        value: data?.addressLine1 || "N/A",
        className: "company-address-line1",
      },
      {
        field: "Address Line 2",
        value: data?.addressLine2 || "N/A",
        className: "company-address-line2",
      },
      { field: "City", value: data?.city || "N/A", className: "company-city" },
      {
        field: "State",
        value: data?.state || "N/A",
        className: "company-state",
      },
      {
        field: "Country",
        value: data?.country || "N/A",
        className: "company-country",
      },
      {
        field: "Pin Code",
        value: data?.pincode || "N/A",
        className: "company-pincode",
      },
      {
        field: "Email",
        value: data?.email || "N/A",
        className: "company-email",
      },
      {
        field: "Color Theme",
        value: (
          <div className="color-theme-display">
            <div
              className="color-theme-preview"
              style={{ backgroundColor: data?.colorTheme || "#007bff" }}
            ></div>
            <span>{data?.colorTheme || "#007bff"}</span>
          </div>
        ),
        className: "company-color-theme",
      },
      {
        field: "Logo",
        value: data?.logo ? (
          <img
            src={`data:${data.logoType || "image/png"};base64,${data.logo}`}
            alt="Company Logo"
            className="company-logo-image"
          />
        ) : (
          <img
            src={logo}
            alt="Default Logo"
            className="company-logo-image default-logo"
          />
        ),
        className: "company-logo",
      },
    ];
  };

  const formattedData = formatCompanyData(companyDetailData);

  return isEditing ? (
    // Edit Form View
    <div className="company-detail">
      <Heading className="display6">Edit Company Details</Heading>
      {error && <div className="error-message">{error}</div>}
      <Formik
        initialValues={{
          companyName: companyDetailData?.companyName || "N/A",
          ceo: companyDetailData?.ceo || "",
          empIDPrefix: companyDetailData?.empIDPrefix || "",
          addressLine1: companyDetailData?.addressLine1 || "",
          addressLine2: companyDetailData?.addressLine2 || "",
          city: companyDetailData?.city || "",
          state: companyDetailData?.state || "",
          country: companyDetailData?.country || "",
          pincode: companyDetailData?.pincode || "",
          email: companyDetailData?.email || "",
          logo: null,
        }}
        validationSchema={companyDetailValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form className="company-detail-form">
            {/* First Column Fields */}
            {[
              "companyName",
              "ceo",
              "empIDPrefix",
              "city",
              "state",
              "country",
              "pincode",
              "email",
            ].map((field, index) => (
              <div key={index} className="form-group">
                <label htmlFor={field} className="form-label">
                  {field.charAt(0).toUpperCase() +
                    field.slice(1).replace(/([A-Z])/g, " $1")}
                </label>
                <Field name={field} type="text" className="form-input" />
                <ErrorMessage
                  name={field}
                  component="div"
                  className="error-message"
                />
              </div>
            ))}

            {/* Second Column: Address Line 1 and 2 */}
            <div className="form-group address-line-group address-line1">
              <label htmlFor="addressLine1" className="form-label">
                Address Line 1
              </label>
              <Field name="addressLine1" type="text" className="form-input" />
              <ErrorMessage
                name="addressLine1"
                component="div"
                className="error-message"
              />
            </div>
            <div className="form-group address-line-group address-line2">
              <label htmlFor="addressLine2" className="form-label">
                Address Line 2
              </label>
              <Field name="addressLine2" type="text" className="form-input" />
              <ErrorMessage
                name="addressLine2"
                component="div"
                className="error-message"
              />
            </div>

            <div className="form-group">
              <label htmlFor="company-logo-upload" className="form-label">
                Logo <span className="optional-field">(Optional)</span>
                <span> (MAX:500 KB)</span>
              </label>
              <FileInput
                name="company-logo-upload"
                label="Choose company logo"
                onFileSelect={(file) => setFieldValue("logo", file)}
              />
              <ErrorMessage
                name="logo"
                component="div"
                className="error-message"
              />
            </div>

            <div className="form-buttons">
              <ButtonWithIcon
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
                icon={"tdesign:send"}
                text={isSubmitting ? "Saving..." : "Save"}
              />
              <ButtonWithIcon
                type="button"
                onClick={() => setIsEditing(false)}
                className="cancel-button"
                icon={"ph:x-circle"}
                text="Cancel"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  ) : (
    // View Mode
    <div className="company-detail">
      <div className="action-button-companyDetails">
        <ButtonWithIcon
          type="button"
          onClick={() => setIsEditing(true)}
          className="edit-button"
          icon={"tdesign:edit"}
          text="Edit Details"
        />
        <ButtonWithIcon
          type="button"
          onClick={() => setIsEditingColorTheme(true)}
          icon={"mdi:palette"}
          text="Edit Theme"
        />
      </div>

      <Heading className="display6">Company Details</Heading>
      {/* Color Theme Editing Section */}
      {isEditingColorTheme && (
        <div className="color-theme-edit-section">
          <Heading className="display7">Edit Color Theme</Heading>
          <div className="color-theme-warning">
            <span className="warning-icon">🚫</span>
            <span className="warning-text">
              "White/Black, off-white/black, and very light/black colors are not
              allowed as they cause visibility issues. Please choose a
              darker/lighter color.",
            </span>
          </div>
          {colorWarning && (
            <div
              className="color-warning-message"
              style={{
                color: "#d32f2f",
                backgroundColor: "#ffebee",
                padding: "10px",
                borderRadius: "4px",
                marginBottom: "15px",
                border: "1px solid #f8d7da",
              }}
            >
              {colorWarning}
            </div>
          )}
          <Formik
            initialValues={{
              colorTheme: companyDetailData?.colorTheme || "#007bff",
            }}
            validationSchema={colorThemeValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
              handleColorThemeUpdate(values.colorTheme);
              setSubmitting(false);
            }}
          >
            {({ setFieldValue, values }) => (
              <Form className="color-theme-form">
                <div className="form-group">
                  <label htmlFor="colorTheme" className="form-label">
                    Color Theme (Dark colors only)
                  </label>
                  <div className="color-picker-container">
                    <Field name="colorTheme">
                      {({ field }) => (
                        <div className="color-input-wrapper">
                          <input
                            type="color"
                            {...field}
                            className="color-picker-input"
                            onChange={(e) => {
                              handleColorChange(e.target.value, setFieldValue);
                            }}
                          />
                          <input
                            type="text"
                            {...field}
                            className="form-input color-text-input"
                            placeholder="#007bff"
                            onChange={(e) => {
                              handleColorChange(e.target.value, setFieldValue);
                            }}
                            style={{
                              borderColor: isColorTooLight(values.colorTheme)
                                ? "#d32f2f"
                                : undefined,
                            }}
                          />

                          <div className="color-theme-buttons">
                            <ButtonWithIcon
                              type="submit"
                              className="save-theme-button"
                              disabled={
                                colorThemeLoading ||
                                isColorTooLight(values.colorTheme)
                              }
                              icon={"tdesign:send"}
                              text={
                                colorThemeLoading ? "Saving..." : "Save Theme"
                              }
                            />
                            <ButtonWithIcon
                              type="button"
                              onClick={() => {
                                setIsEditingColorTheme(false);
                                setColorWarning("");
                              }}
                              className="cancel-button"
                              icon={"ph:x-circle"}
                              text="Cancel"
                            />
                          </div>
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

      <div className="company-logo-container">
        {formattedData[formattedData.length - 1].value}{" "}
        {/* Logo is last item */}
        <div className="company-info">
          <h2 className="company-name">{companyDetailData.companyName}</h2>
          <h4 className="company-ceo">{companyDetailData.ceo}</h4>
        </div>
      </div>

      <hr className="separator" />
      <div className="company-details-view">
        {formattedData.slice(2, -1).map((detail, index) => (
          <div
            key={index}
            className={`company-detail-item ${detail.className}`}
          >
            <strong>{detail.field}:</strong> {detail.value}
          </div>
        ))}
      </div>
      <WarningPopup
        isOpen={showDeleteWarning}
        onClose={() => setShowDeleteWarning(false)}
        onAccept={handleDeleteSampleData}
        onDecline={() => setShowDeleteWarning(false)}
        title="Delete Sample Data"
        message="⚠️ WARNING: This action will permanently delete ALL sample data from the system. This includes test employees, departments, and all related records. This action cannot be undone. Are you absolutely sure you want to proceed?"
        acceptText={isDeleting ? "Deleting..." : "Yes, Delete All"}
        declineText="Cancel"
      />
    </div>
  );
};

export default CompanyDetail;
